import type { ReactElement } from 'react';
import { cn } from '../../../lib/cn';
import { CommentBankCard } from './CommentBankCard';
import { FeedbackCard } from './FeedbackCard';
import { RubricCard } from './RubricCard';
import { ScoreCard } from './ScoreCard';
import { StandardsEvidenceCard } from './StandardsEvidenceCard';
import { GradeActionFooter } from './GradeActionFooter';

type GradingSidebarProps = {
  rubricText?: string;
  scoreValue: string;
  onScoreChange: (v: string) => void;
  maxScore: number;
  feedbackValue: string;
  onFeedbackChange: (v: string) => void;
  commentTemplates: string[];
  onInsertComment: (text: string) => void;
  onRubricCriterionSelect?: (line: string) => void;
  standardsLines?: string[];
  disabled?: boolean;
  /** When set, only render these sections (mobile rubric / feedback tabs). */
  sections?: 'all' | 'rubric' | 'feedback';
  className?: string;
  onSaveDraft?: () => void;
  onSaveAndNext?: () => void;
  onFinalize?: () => void;
  onRescan?: () => void;
  onDelete?: () => void;
  saveNextDisabled?: boolean;
};

export function GradingSidebar({
  rubricText,
  scoreValue,
  onScoreChange,
  maxScore,
  feedbackValue,
  onFeedbackChange,
  commentTemplates,
  onInsertComment,
  onRubricCriterionSelect,
  standardsLines,
  disabled = false,
  sections = 'all',
  className,
  onSaveDraft,
  onSaveAndNext,
  onFinalize,
  onRescan,
  onDelete,
  saveNextDisabled,
}: GradingSidebarProps): ReactElement {
  const showRubric = sections === 'all' || sections === 'rubric';
  const showFeedback = sections === 'all' || sections === 'feedback';

  return (
    <aside
      className={cn(
        'flex min-h-0 w-full min-w-0 flex-col border-l border-slate-200/80 bg-dg-bg-surface dark:border-slate-700/70 dark:bg-slate-900/25 lg:border-l-0',
        className
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden overflow-x-hidden p-4">
        {showRubric ? (
          <>
            <ScoreCard
              scoreValue={scoreValue}
              onScoreChange={onScoreChange}
              maxScore={maxScore}
              disabled={disabled}
              size="large"
            />
            <RubricCard rubricText={rubricText} onCriterionSelect={onRubricCriterionSelect} />
          </>
        ) : null}
        {showFeedback ? (
          <>
            <FeedbackCard
              value={feedbackValue}
              onChange={onFeedbackChange}
              disabled={disabled}
              size="large"
            />
            {commentTemplates.length > 0 ? (
              <CommentBankCard templates={commentTemplates} onInsert={onInsertComment} />
            ) : null}
            <StandardsEvidenceCard lines={standardsLines} />
          </>
        ) : null}
      </div>
      {onSaveDraft && onSaveAndNext && onFinalize ? (
        <GradeActionFooter
          onSaveDraft={onSaveDraft}
          onSaveAndNext={onSaveAndNext}
          onFinalize={onFinalize}
          onRescan={onRescan}
          onDelete={onDelete}
          saveNextDisabled={saveNextDisabled}
        />
      ) : null}
    </aside>
  );
}
